import { isAxiosError, isCancel, type AxiosInstance } from "axios";
import { create } from "zustand";

/**
 * Backend-outage guard.
 *
 * When the API is down every query used to retry forever (polling + focus
 * refetch re-armed each failure), which piled up thousands of requests a minute
 * and left the user on a blank dark screen. This module is the single place
 * that decides (a) whether a failed request is worth retrying at all, (b) how
 * many times, and (c) when to give up and show the "Something went wrong" page.
 *
 * Two paths feed it, so both react-query and plain axios calls are covered:
 * - react-query reads `shouldRetryRequest` / `backendRetryDelay` as its global
 *   defaults (see app.tsx).
 * - the axios response interceptor reports every settled request (see api.ts),
 *   so a single query exhausting its retries — or a burst of failures across
 *   many calls with no success in between — trips the page.
 */

/**
 * Hard ceiling on retries per request. The outage page shows once a call has
 * failed this many times in a row. Five survives a backend restart or a short
 * network drop; a real outage lasts minutes, so more only prolongs the wait.
 */
export const MAX_BACKEND_RETRIES = 5;

/** Backoff: 1s, 2s, then capped — the retries are spent in about 9 seconds. */
export const BACKEND_RETRY_DELAY_CAP_MS = 2_000;

/**
 * Failures that mean "the backend is not answering" — a network error (no
 * response at all), a timeout, or a 5xx. These are the only ones worth
 * retrying; a 4xx will answer the same way every time.
 */
export function isBackendUnreachableError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  if (isCancel(error)) return false;
  const status = error.response?.status;
  if (status === undefined) return true;
  return status >= 500;
}

/** react-query `retry` default: only unreachable-type errors, at most MAX_BACKEND_RETRIES times. */
export function shouldRetryRequest(failureCount: number, error: unknown): boolean {
  if (useBackendHealthStore.getState().unavailable) return false;
  return failureCount < MAX_BACKEND_RETRIES && isBackendUnreachableError(error);
}

/** react-query `retryDelay` default. */
export function backendRetryDelay(attempt: number): number {
  return Math.min(1_000 * 2 ** attempt, BACKEND_RETRY_DELAY_CAP_MS);
}

interface BackendHealthStore {
  /** True once the outage page should replace the app. Cleared by logout / try again. */
  unavailable: boolean;
  /** Unreachable-type failures since the last successful backend response. */
  consecutiveFailures: number;
  recordSuccess: () => void;
  recordFailure: (error: unknown) => void;
  reset: () => void;
}

export const useBackendHealthStore = create<BackendHealthStore>()((set, get) => ({
  unavailable: false,
  consecutiveFailures: 0,
  recordSuccess: () => {
    if (get().consecutiveFailures !== 0) set({ consecutiveFailures: 0 });
  },
  recordFailure: (error) => {
    if (!isBackendUnreachableError(error)) return;
    const { unavailable, consecutiveFailures } = get();
    if (unavailable) return;
    const next = consecutiveFailures + 1;
    // The first attempt plus MAX_BACKEND_RETRIES retries — once that many calls
    // fail back to back, the backend is down and retrying is only load.
    set({
      consecutiveFailures: next,
      unavailable: next > MAX_BACKEND_RETRIES,
    });
  },
  reset: () => set({ unavailable: false, consecutiveFailures: 0 }),
}));

/**
 * Feed every settled request of an axios instance into the guard. Installed
 * on the app instance and the guest (client portal / strategy share) instance
 * so plain axios calls — SSE chat, uploads — count, not only react-query ones.
 */
export function trackBackendHealth(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response) => {
      useBackendHealthStore.getState().recordSuccess();
      return response;
    },
    (error) => {
      useBackendHealthStore.getState().recordFailure(error);
      return Promise.reject(error);
    },
  );
}
